/**
 * 契約詳細の状態と操作（プロダクト仕様書「契約詳細」「保存・削除に失敗したとき」）。
 *
 * 追加・編集はダイアログで入力し、保存の前に確認の段で内容を示す。削除も確認してから行う。
 * DBに反映できたかが分からない旨を示した後は、入力を変えられないようにし、同じ内容のままの再度の保存・削除と、
 * 入力内容を破棄して契約の値を取得し直すことだけをできるようにする。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAuthHeader } from '@/hooks/useAuth';
import * as api from '@/lib/api/credit-contracts';
import type {
  ContractDetail,
  ContractTable,
  DataConnectRow,
  PlanRow,
  RowInput,
} from '@/lib/api/credit-contracts';
import {
  adjustTarget,
  checkFields,
  hasErrors,
  periodOf,
  type EditMode,
  type FieldErrors,
  type FieldKey,
} from '@/lib/credit-contracts/validation';
import { monthLabel } from '@/lib/credit-contracts/month';

import { actionFailure, fetchFailureMessage, type ActionFailure } from './display';
import type { Load } from './useContractList';

export type ContractRow = PlanRow | DataConnectRow;

export interface EditDialog {
  kind: 'edit';
  table: ContractTable;
  /** 編集する行（追加では null） */
  original: ContractRow | null;
  mode: EditMode;
  input: RowInput;
  errors: FieldErrors;
  /** 入力欄に属さないエラー */
  generalErrors: string[];
  step: 'input' | 'confirm';
  /** 追加で終了月を合わせる行 */
  adjust: ContractRow | null;
  failure: ActionFailure | null;
  /**
   * DBに反映できたかが分からない旨を示した後。ダイアログを閉じるまで、後の失敗が何であっても解かない。
   * 入力を変えられず、同じ内容のままの再度の保存と、入力内容を破棄して取得し直すことだけができる
   */
  frozen: boolean;
}

export interface DeleteDialog {
  kind: 'delete';
  table: ContractTable;
  original: ContractRow;
  failure: ActionFailure | null;
  frozen: boolean;
}

export type Dialog = EditDialog | DeleteDialog;

/** 編集・追加の入力欄の初期値。月は画面で入力する形（`YYYY/MM`）にする */
function initialInput(table: ContractTable, row: ContractRow | null): RowInput {
  const start_month = row ? monthLabel(row.start_month) : '';
  const end_month = row?.end_month ? monthLabel(row.end_month) : '';
  if (table === 'data-connect-credits') {
    return {
      start_month,
      end_month,
      monthly_credit: (row as DataConnectRow | null)?.monthly_credit ?? '',
    };
  }
  const plan = row as PlanRow | null;
  return {
    start_month,
    end_month,
    // 追加のときは、どちらも選んでいない状態で始める
    pricing_plan: plan?.pricing_plan ?? '',
    monthly_contract_credit: plan?.monthly_contract_credit ?? '',
    credit_unit_price: plan?.credit_unit_price ?? '',
    monthly_fee: plan?.monthly_fee ?? '',
  };
}

function editMode(table: ContractTable, row: ContractRow | null): EditMode {
  if (table === 'pricing-plans') return { table };
  if (!row) return { table, kind: 'add' };
  return { table, kind: (row as DataConnectRow).category === 'active' ? 'active' : 'scheduled' };
}

export function rowsOf(detail: ContractDetail, table: ContractTable): ContractRow[] {
  return table === 'pricing-plans' ? detail.pricing_plans : detail.data_connect_credits;
}

export function useContractDetail(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;
  const [detail, setDetail] = useState<Load<ContractDetail>>({ status: 'loading' });
  // 表示している値があるときの取得中。取得し終えるまで前の値の表を残し、表のスクロールの位置を保つ
  const [refreshing, setRefreshing] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [busy, setBusy] = useState(false);
  const sequence = useRef(0);

  const load = useCallback(async () => {
    const id = ++sequence.current;
    setDetail((current) =>
      current.status === 'ok' && current.data.company.id === companyId
        ? current
        : { status: 'loading' },
    );
    setRefreshing(true);
    try {
      const data = await api.getContract(companyId, authRef.current());
      if (id === sequence.current) {
        setDetail({ status: 'ok', data });
        setRefreshing(false);
      }
    } catch (e) {
      if (id === sequence.current) {
        setDetail({ status: 'error', message: fetchFailureMessage(api.toFailure(e)) });
        setRefreshing(false);
      }
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = (table: ContractTable, row: ContractRow | null) =>
    setDialog({
      kind: 'edit',
      table,
      original: row,
      mode: editMode(table, row),
      input: initialInput(table, row),
      errors: {},
      generalErrors: [],
      step: 'input',
      adjust: null,
      failure: null,
      frozen: false,
    });

  const openDelete = (table: ContractTable, row: ContractRow) =>
    setDialog({ kind: 'delete', table, original: row, failure: null, frozen: false });

  const current = detail.status === 'ok' ? detail.data.current_month : '';

  /** 入力を変える。エラーのある入力欄は、値が変わるたびにチェックをやり直す */
  const change = (key: FieldKey, value: string) =>
    setDialog((d) => {
      if (!d || d.kind !== 'edit' || d.frozen) return d;
      const input: RowInput = { ...d.input, [key]: value };
      const errors: FieldErrors = { ...d.errors };
      // 料金プランで使わない入力欄は空にする（入力できない欄の値を送らない）
      if (key === 'pricing_plan') {
        const unused: ('monthly_fee' | 'monthly_contract_credit' | 'credit_unit_price')[] =
          value === 'Credit' ? ['monthly_fee'] : ['monthly_contract_credit', 'credit_unit_price'];
        for (const k of unused) {
          input[k] = '';
          delete errors[k];
        }
      }
      const rechecked = checkFields(input, d.mode, current);
      for (const k of Object.keys(errors) as FieldKey[]) {
        if (rechecked[k]) errors[k] = rechecked[k];
        else delete errors[k];
      }
      return { ...d, input, errors };
    });

  /** 「確認へ」。入力欄ごとのチェックでエラーがなければ、保存の前の確認を示す */
  const toConfirm = () =>
    setDialog((d) => {
      if (!d || d.kind !== 'edit' || d.frozen || detail.status !== 'ok') return d;
      const errors = checkFields(d.input, d.mode, current);
      if (hasErrors(errors)) return { ...d, errors, generalErrors: [], failure: null };
      const adjust = d.original
        ? null
        : adjustTarget(rowsOf(detail.data, d.table), periodOf(d.input));
      return { ...d, errors, generalErrors: [], failure: null, adjust, step: 'confirm' };
    });

  const backToInput = () =>
    setDialog((d) =>
      d && d.kind === 'edit' && !d.frozen ? { ...d, step: 'input', failure: null } : d,
    );

  /** ダイアログを閉じて、契約の値を取得し直す */
  const refetch = () => {
    setDialog(null);
    void load();
  };

  /** ダイアログを閉じる。反映できたかが分からない後や、取得し直す必要があるときは、取得し直す */
  const close = () => {
    if (busy) return;
    if (dialog?.frozen || (dialog?.failure && dialog.failure.kind !== 'retry')) refetch();
    else setDialog(null);
  };

  const finishSuccess = async (message: string) => {
    setDialog(null);
    toast.success(message);
    await load();
  };

  const save = async () => {
    if (!dialog || dialog.kind !== 'edit' || detail.status !== 'ok') return;
    const d = dialog;
    setBusy(true);
    // 前回の保存のエラーと失敗の表示は、保存し直すときに消す
    setDialog((prev) =>
      prev && prev.kind === 'edit'
        ? { ...prev, errors: {}, generalErrors: [], failure: null }
        : prev,
    );
    const auth = authRef.current();
    const current_month = detail.data.current_month;
    try {
      if (d.original) {
        await api.updateRow(
          companyId,
          d.table,
          d.original.start_month,
          { current_month, version: d.original.version, row: d.input },
          auth,
        );
      } else {
        const adjust = d.adjust
          ? { start_month: d.adjust.start_month, version: d.adjust.version }
          : null;
        await api.createRow(companyId, d.table, { current_month, row: d.input, adjust }, auth);
      }
      await finishSuccess('保存しました。');
    } catch (e) {
      const f = api.toFailure(e);
      setDialog((prev) => {
        if (!prev || prev.kind !== 'edit') return prev;
        if (f.code === 'VALIDATION_ERROR') {
          const errors: FieldErrors = {};
          const generalErrors: string[] = [];
          for (const v of f.violations) {
            if (v.field) errors[v.field] ??= v.message;
            else generalErrors.push(v.message);
          }
          // 反映できたかが分からない後は、入力に戻さず、確認の段でエラーを示す
          const step = prev.frozen ? 'confirm' : 'input';
          return { ...prev, errors, generalErrors, step, failure: null };
        }
        const failure = actionFailure(prev.original ? 'update' : 'create', f);
        return { ...prev, failure, frozen: prev.frozen || failure.kind === 'unknown' };
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!dialog || dialog.kind !== 'delete' || detail.status !== 'ok') return;
    const d = dialog;
    setBusy(true);
    setDialog((prev) => (prev ? { ...prev, failure: null } : prev));
    try {
      await api.deleteRow(
        companyId,
        d.table,
        d.original.start_month,
        { current_month: detail.data.current_month, version: d.original.version },
        authRef.current(),
      );
      await finishSuccess('削除しました。');
    } catch (e) {
      const failure = actionFailure('delete', api.toFailure(e));
      setDialog((prev) =>
        prev ? { ...prev, failure, frozen: prev.frozen || failure.kind === 'unknown' } : prev,
      );
    } finally {
      setBusy(false);
    }
  };

  return {
    detail,
    refreshing: refreshing && detail.status === 'ok',
    dialog,
    busy,
    load,
    openCreate: (table: ContractTable) => openEdit(table, null),
    openEdit,
    openDelete,
    change,
    toConfirm,
    backToInput,
    save,
    remove,
    close,
    refetch,
  };
}
